import styled, { css } from "styled-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import { ChevronDownIcon } from "@boi/react-icons";

export const HeaderWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        &:has(:not([disabled]):focus-visible) {
          border-radius: ${select.button.focusRing.radius};
          outline-width: ${select.button.focusRing.width};
          outline-style: ${select.button.focusRing.style};
          outline-color: ${select.button.focusRing.color};
          outline-offset: ${select.button.focusRing.offset};
        }
      `,
    ];
  }}
`;

type HeaderProps = {
  $isOpen: boolean;
  $hasError?: boolean;
  $isDisabled?: boolean;
};

export const Header = styled.div<HeaderProps>`
  ${({ $isOpen, $hasError, $isDisabled }) => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        display: grid;
        min-height: ${select.button.minHeight};
        background: ${select.button.backgroundColor.base};
        padding-top: ${select.button.padding.top};
        padding-left: ${select.button.padding.left};
        padding-right: ${select.button.padding.right};
        padding-bottom: ${select.button.padding.bottom};
        border-radius: ${select.button.stroke.radius};
        outline-width: ${select.button.stroke.width.base};
        outline-style: ${select.button.stroke.style};
        outline-color: ${select.button.stroke.color.base};
        outline-offset: ${select.button.stroke.offset.base};

        svg {
          color: ${select.button.icon.color.base};
        }

        &:hover,
        &:focus {
          outline-width: ${select.button.stroke.width.hover};
          outline-color: ${select.button.stroke.color.hover};
          outline-offset: ${select.button.stroke.offset.hover};

          svg {
            color: ${select.button.icon.color.hover};
          }
        }
      `,
      $hasError &&
        css`
          &,
          &:hover,
          &:focus {
            outline-width: ${select.button.stroke.width.error};
            outline-color: ${select.button.stroke.color.error};
            outline-offset: ${select.button.stroke.offset.error};
          }
        `,
      $isOpen &&
        css`
          &,
          &:hover,
          &:focus {
            outline-width: ${select.button.stroke.width.active};
            outline-color: ${select.button.stroke.color.active};
            outline-offset: ${select.button.stroke.offset.active};

            svg {
              color: ${select.button.icon.color.active};
            }
          }
        `,
      $isDisabled &&
        css`
          cursor: not-allowed;

          &,
          &:hover,
          &:focus {
            outline-width: ${select.button.stroke.width.disabled};
            outline-color: ${select.button.stroke.color.disabled};
            outline-offset: ${select.button.stroke.offset.disabled};

            svg {
              color: ${select.button.icon.color.disabled};
            }
          }
        `,
    ];
  }}
`;

export const HeaderContent = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: ${select.button.gap};
    `;
  }}
`;

export const Button = styled.button`
  all: unset;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

export const ButtonIconWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      display: grid;
      place-items: center;

      svg {
        width: ${select.button.icon.width};
        height: ${select.button.icon.height};
      }
    `;
  }}
`;

export const ButtonClosedIcon = styled(ChevronDownIcon)`
  rotate: 0deg;
`;

export const ButtonOpenIcon = styled(ButtonClosedIcon)`
  rotate: 180deg;
`;

type ListContainerProps = {
  $isOpen: boolean;
};

export const ListContainer = styled.div<ListContainerProps>`
  ${({ $isOpen }) => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      display: ${$isOpen ? "grid" : "none"};
      width: 0;
      overflow: hidden;
      border-radius: ${select.menu.radius};
      border-width: ${select.menu.stroke.width};
      border-style: ${select.menu.stroke.style};
      border-color: ${select.menu.stroke.color};
      box-shadow: 0px 2px 2px ${select.menu.shadow.outer.color};
      z-index: ${select.menu.zIndex};
    `;
  }}
`;

export const List = styled.ul`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      all: unset;
      display: grid;
      gap: ${select.menu.separator.width};
      background: ${select.menu.separator.color};
    `;
  }}
`;
